import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'inside-out/components/shared/link';
import { ArrowLink } from 'inside-out/components/shared/arrowLink';

import { Helmet } from 'react-helmet';

import { fullAudiences } from 'inside-out/managers/audienceManager';
import { extractAudiences } from 'inside-out/managers/resourceLibraryManager';
import { idToLink } from 'inside-out/managers/navManager';

import { PageHeader } from 'inside-out/components/shared/pageHeader';
import { Cards } from 'inside-out/components/shared/cards';
import { ELearningCard } from 'inside-out/components/shared/cards/eLearningCard';
import { ResourceCard } from 'inside-out/components/shared/cards/resourceCard';
import { NewsCard } from 'inside-out/components/shared/cards/newsCard';
import { GetInvolved } from 'inside-out/components/shared/getInvolved';

import ProfilesBG from 'inside-out/images/profiles/bg.svg';
import EqualsIcon from 'inside-out/images/equals-icon.svg';
import CircleUnderlineIcon from 'inside-out/images/circle-underline-icon.svg';
import CircleOverlineIcon from 'inside-out/images/circle-overline-icon.svg';
import PlusIcon from 'inside-out/images/plus-icon.svg';

import {
	innerWrapperStyle,
	sectionsStyle,
	subTitleStyle,
	quadrantsWrapper,
} from 'inside-out/page-styles/health-professionals';

import { grey, blue, cardSection, contentSection } from 'inside-out/global-styles';

const resourcePropType = PropTypes.shape({
	edges: PropTypes.arrayOf(
		PropTypes.shape({
			node: PropTypes.shape({
				title: PropTypes.string,
				type: PropTypes.string,
				attachments: PropTypes.arrayOf(
					PropTypes.shape({
						file: PropTypes.string,
					})
				),
				date: PropTypes.string,
				thumbnail: PropTypes.string,
			}).isRequired,
		}).isRequired
	),
}).isRequired;

export default class HealthProfessionalsPage extends React.PureComponent {
	static propTypes = {
		data: PropTypes.shape({
			page: PropTypes.shape({
				edges: PropTypes.arrayOf(
					PropTypes.shape({
						node: PropTypes.shape({
							main_heading: PropTypes.string.isRequired,
							main_body: PropTypes.string.isRequired,
							content_heading_1: PropTypes.string.isRequired,
							content_body_1: PropTypes.string.isRequired,
							content_link_text_1: PropTypes.string.isRequired,
							content_link_url_1: PropTypes.string.isRequired,
							content_heading_2: PropTypes.string.isRequired,
							content_body_2: PropTypes.string.isRequired,
							content_link_text_2: PropTypes.string.isRequired,
							content_link_url_2: PropTypes.string.isRequired,
							content_heading_3: PropTypes.string.isRequired,
							content_body_3: PropTypes.string.isRequired,
							content_link_text_3: PropTypes.string.isRequired,
							content_link_url_3: PropTypes.string.isRequired,
							content_heading_4: PropTypes.string.isRequired,
							content_body_4: PropTypes.string.isRequired,
							content_link_text_4: PropTypes.string.isRequired,
							content_link_url_4: PropTypes.string.isRequired,
						}),
					})
				),
			}),
			recentELearnings: resourcePropType,
			recentResources: resourcePropType,
		}).isRequired,
	};

	render() {
		const pageSettings = this.props.data.page.edges[0].node;
		const eLearnings = [
			pageSettings.eLearning_1,
			pageSettings.eLearning_2,
			pageSettings.eLearning_3,
		].filter(l => l);

		const resources = [
			pageSettings.resource_1,
			pageSettings.resource_2,
			pageSettings.resource_3,
			pageSettings.resource_4,
		].filter(r => r);

		const events = [
			pageSettings.event_1,
			pageSettings.event_2,
			pageSettings.event_3,
			pageSettings.event_4,
		].filter(e => e);

		const blogs = [
			pageSettings.blog_1,
			pageSettings.blog_2,
			pageSettings.blog_3,
			pageSettings.blog_4,
		].filter(b => b);

		return (
			<React.Fragment>
				<Helmet title={pageSettings.main_heading}>
          <meta property="og:type" content="website"/>
          <meta property="og:title" content={pageSettings.main_heading}/>
          <meta name="description" content={pageSettings.main_body}/>
          <meta property="og:description" content={pageSettings.main_body}/>
        </Helmet>

				<PageHeader
					background={`#fff url(${ProfilesBG}) no-repeat bottom right`}
					titleText={pageSettings.main_heading}
					titleTextColor={blue}
					subTitleText={pageSettings.main_body}
					subTitleTextColor={grey}
				/>
				<div className={sectionsStyle}>

					{/* eLearning */}
					{ eLearnings.length > 0 && (
						<section className={contentSection()}>
							<div className={innerWrapperStyle}>
								<div className={cardSection(0, true)}>
									<header>
										<h1 className={subTitleStyle}>eLearning</h1>
										<Link to="/e-learning">View all</Link>
									</header>
									<Cards arrowTo="/e-learning">
										{eLearnings.map(node => (
											<ELearningCard
												key={node.id}
												to={idToLink(node.id)}
												title={node.title}
												body={node.extract}
												linkText={node.tile_button_text}
												hasVideo={!!node.attachments.find(a => a.vimeo_url)}
												mediaImage={
													node.fields && node.fields.thumbnail_image.childImageSharp.resize.src
												}
											/>
										))}
									</Cards>
								</div>
							</div>
						</section>
					) }

					{/* Resources */}
					{ resources.length > 0 && (
						<section className={contentSection()}>
							<div className={innerWrapperStyle}>
								<div className={cardSection(0, true)}>
									<header>
										<h1 className={subTitleStyle}>Resources</h1>
										<Link to="/resource-library">View all</Link>
									</header>
									<Cards columns={4} arrowTo="/resource-library">
										{resources.map(node => (
											<ResourceCard
												key={node.id}
												to={idToLink(node.id)}
												background={
													node.fields && node.fields.thumbnail_image.childImageSharp.resize.src
												}
												audiences={extractAudiences(node.categories)}
												hasVideo={!!node.attachments.find(a => a.vimeo_url)}
												title={node.title}
												body={node.extract}
											/>
										))}
									</Cards>
								</div>
							</div>
						</section>
					) }

					{/* News & Events */}
					{ events.length > 0 && (
						<section className={contentSection('#fff')}>
							<div className={innerWrapperStyle}>
								<div className={cardSection(0, true)}>
									<header>
										<h1 className={subTitleStyle}>News and Events</h1>
										<Link to="/news-and-events">View all</Link>
									</header>
									<Cards columns={4} arrowTo="/news-and-events">
										{events.map(node => (
											<NewsCard
												key={node.id}
												to={idToLink(node.id)}
												title={node.title}
												body={node.extract}
												upcoming={node.type}
												upcomingBackgroundColor={grey}
												date={node.start_time}
												endDate={node.end_time}
												background={
													node.fields && node.fields.thumbnail_image.childImageSharp.resize.src
												}
											/>
										))}
									</Cards>
								</div>
							</div>
						</section>
					) }

					{/* Blog */}
					{ blogs.length > 0 && (
						<section className={contentSection('#fff')}>
							<div className={innerWrapperStyle}>
								<div className={cardSection(0, true)}>
									<header>
										<h1 className={subTitleStyle}>Blog and Podcasts</h1>
										<Link to="/blog">View all</Link>
									</header>
									<Cards columns={4} arrowTo="/blog">
										{blogs.map(node => (
											<ResourceCard
												key={node.id}
												background={
													node.fields &&
													node.fields.thumbnail_image &&
													node.fields.thumbnail_image.childImageSharp.resize.src
												}
												to={idToLink(node.id)}
												audiences={fullAudiences(node.audiences)}
												title={node.title}
												body={node.extract}
												date={node.date}
											/>
										))}
									</Cards>
								</div>
							</div>
						</section>
					) }
				</div>

				<div className={innerWrapperStyle}>
					<div className={quadrantsWrapper}>
						<section>
							<img src={EqualsIcon} alt="" />
							<h1>{pageSettings.content_heading_1}</h1>
							<div dangerouslySetInnerHTML={{ __html: pageSettings.content_body_1 }} />
							<ArrowLink to={pageSettings.content_link_url_1}>
								{pageSettings.content_link_text_1}
							</ArrowLink>
						</section>
						<section>
							<img src={CircleOverlineIcon} alt="" />
							<h1>{pageSettings.content_heading_2}</h1>
							<div dangerouslySetInnerHTML={{ __html: pageSettings.content_body_2 }} />
							<ArrowLink to={pageSettings.content_link_url_2}>
								{pageSettings.content_link_text_2}
							</ArrowLink>
						</section>
						<section>
							<img src={CircleUnderlineIcon} alt="" />
							<h1>{pageSettings.content_heading_3}</h1>
							<div dangerouslySetInnerHTML={{ __html: pageSettings.content_body_3 }} />
							<ArrowLink to={pageSettings.content_link_url_3}>
								{pageSettings.content_link_text_3}
							</ArrowLink>
						</section>
						<section>
							<img src={PlusIcon} alt="" />
							<h1>{pageSettings.content_heading_4}</h1>
							<div dangerouslySetInnerHTML={{ __html: pageSettings.content_body_4 }} />
							<ArrowLink to={pageSettings.content_link_url_4}>
								{pageSettings.content_link_text_4}
							</ArrowLink>
						</section>
					</div>
				</div>

				<GetInvolved />
			</React.Fragment>
		);
	}
}

export const query = graphql`
	query HealthProfessionalsLandingQuery {
		page: allContentJson(
			filter: { id: { regex: "/content\\/health-professionals-page\\.json/" } }
		) {
			edges {
				node {
					main_heading
					main_body
					content_heading_1
					content_body_1
					content_link_text_1
					content_link_url_1
					content_heading_2
					content_body_2
					content_link_text_2
					content_link_url_2
					content_heading_3
					content_body_3
					content_link_text_3
					content_link_url_3
					content_heading_4
					content_body_4
					content_link_text_4
					content_link_url_4
					eLearning_1 {
						...resource
					}
					eLearning_2 {
						...resource
					}
					eLearning_3 {
						...resource
					}
					resource_1 {
						...resource
					}
					resource_2 {
						...resource
					}
					resource_3 {
						...resource
					}
					resource_4 {
						...resource
					}
					event_1 {
						...event
					}
					event_2 {
						...event
					}
					event_3 {
						...event
					}
					event_4 {
						...event
					}
					blog_1 {
						...blog
					}
					blog_2 {
						...blog
					}
					blog_3 {
						...blog
					}
					blog_4 {
						...blog
					}
				}
			}
		}
	}
`;



// WEBPACK FOOTER //
// ./src/pages/health-professionals/index.js